/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { CreatorFanServiceWidget } from '@marpple/mco-membership-ui';
import { HeaderCreatorShop } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/HeaderCreatorShop.ts';
import { CreatorShopSubHeader } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorShopSubHeader.ts';
import { applySubHeaderHeadroom } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/headroom.ts';
import { html, type Html, ListView, Page, View } from 'rune-ts';
import sanitizer from 'sanitizer';
import { ConfigSentryF } from '../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { MShopUtilF } from '../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { CreatorConstantS, CreatorTypeS } from '../../../features/Creator/outbound/share';
import { FanLetterData } from '../../../features/FanLetter/type';
import { MEMBERSHIP_UI_IDENTIFIER } from '../../../features/Membership/constant';
import { MembershipErrorEnable } from '../../../features/Membership/error/error_enable';
import { MembershipTypeS, MembershipUtilS } from '../../../features/Membership/outbound/share';
import { getActivatedSdkWithEvent } from '../../../features/Membership/util';
import { PhotoReviewCardType } from '../../../features/PhotoReview/type';
import { ProductCardViewData } from '../../../features/ProductList/type';
import { makeEmptyScreenNavigate } from '../../../shared/app/navigate';
import { navigateTo } from '../../../shared/app/util';
import { ClientPopupProps, Popup } from '../../../shared/components/cells/Popup/Popup';
import { classes, doIf, isAllCrewById } from '../../../shared/util';
import { getSharedData } from '../../../shared/util/rune';
import { makePlusUrl } from '../../../shared/util/url';
import { FanLetterItemController } from '../../components/cells/FanLetterItem/controller/FanLetterItemController';
import { FanLetterFormController } from '../../components/cells/FanLetterList/controller/FanLetterFormController';
import { FanLetterListView } from '../../components/cells/FanLetterList/FanLetterListView';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { PhotoReviewCard } from '../../components/cells/PhotoReviewCard/PhotoReviewCard';
import { SeeMoreLinkPlugin } from '../../components/cells/SectionTitle/plugins';
import { SectionTitle } from '../../components/cells/SectionTitle/SectionTitle';
import klass from './StoreMain.module.scss';
import { type LocalsStoreMembership } from '@services/MarppleShop/shared/type/locals.ts';
import { FloatingButton_recent_product } from '@services/MarppleShop/renderApp/components/cells/FloatingBox/FloatingButtons/FloatingButton_recent_product.ts';
import { FundingListResponse } from '@services/MarppleShop/features/Funding';
import { map, pipe, take, toArray } from '@fxts/core';
import { FundingCard } from '@services/MarppleShop/renderApp/components/cells/FundingCard/FundingCard.ts';
import { FundingHelper } from '@services/MarppleShop/renderApp/components/cells/FundingCard/FundingHelper.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { CreatorShopMainBanner24View } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24View.ts';
import { LnbCreatorShop24View } from '@services/MarppleShop/renderApp/components/cells/Header/LnbCreatorShop24View.ts';
import { ECateRootId } from '@services/MarppleShop/shared/enum/ECateRootId.ts';
import { TCreatorShopLnbMenu } from '@services/MarppleShop/shared/constant';
import { CreatorShopMainBanner24MoView } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24MoView.ts';
import { BestCreatorRanking } from '@services/MarppleShop/features/CreatorRank/type/creator-rank.type.ts';
import { CreatorShopProductListEmptyScreen } from '@services/MarppleShop/renderApp/components/cells/ProductCardList/ProductCardListEmptyScreen.ts';
import { ASSET_URLS } from '@services/MarppleShop/shared/constant/asset_urls.ts';
import { CardProductControllerView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductControllerView.ts';

const SHOW_PRODUCT_SEE_MORE_THRESHOLD = 5;
const MOBILE_SHOW_PRODUCT_SEE_MORE_THRESHOLD = 2;

const SHOW_FAN_LETTER_SEE_MORE_THRESHOLD = 4;
const MOBILE_SHOW_FAN_LETTER_SEE_MORE_THRESHOLD = 2;

const SHOW_REVIEW_SEE_MORE_THRESHOLD = 5;
const MOBILE_SHOW_REVIEW_SEE_MORE_THRESHOLD = 2;

type Membership = LocalsStoreMembership & {
  memberships?: MembershipTypeS.MembershipSummary[];
  my: {
    token?: string;
    memberships?: MembershipTypeS.MembershipSummary[];
  };
};

export type StoreMainPageData = {
  membership?: Membership;
  fundings: FundingListResponse | null;
  creator: CreatorTypeS.CreatorMainProfile;
  products: {
    goods: ProductCardViewData[];
    handmade: ProductCardViewData[];
    brand_deal: ProductCardViewData[];
    ebook: ProductCardViewData[];
  };
  photo_reviews: PhotoReviewCardType[];
  menus: TCreatorShopLnbMenu[];
  layout_options: BaseLayoutOptions;
  adult_check_needed?: boolean;
  is_seller_studio?: boolean;
  popup: ClientPopupProps;
  fan_letters: FanLetterData[];
  is_login: boolean;
  recent_product?: ProductCardViewData;
  server_timestamp: number;
  best_store_ranking: BestCreatorRanking | undefined;
  store: {
    id: number;
  };
};

class PhotoReviewList extends ListView<PhotoReviewCard> {
  override className = klass.photo_review_list;
  ItemView = PhotoReviewCard;
}

class MembershipView extends View<{
  creator: CreatorTypeS.CreatorMainProfile;
  membership?: Membership;
  layout_options: BaseLayoutOptions;
  title?: SectionTitle;
}> {
  private _membershipErrorEnable = new MembershipErrorEnable(this);

  protected override template(): Html {
    const { membership_plan_existed, membership_commission_existed, memberships } =
      this.data.membership ?? {};

    return html`<div
      class="${MEMBERSHIP_UI_IDENTIFIER} ${klass.membership}"
      data-memberships="${memberships?.length ?? 0}"
      data-is-only-one-existed="${(membership_plan_existed && !membership_commission_existed) ||
      (!membership_plan_existed && membership_commission_existed)}"
      data-is-all-existed="${membership_plan_existed && membership_commission_existed}"
      data-existed="${membership_plan_existed || membership_commission_existed}"
    >
      <div class="${klass.membership_wrap}">
        <div class="${klass.title}">${this.data.title ?? ''}</div>
      </div>
    </div>`;
  }

  protected override async onRender() {
    const sharedData = getSharedData(this);
    const goToMembershipMain = () => {
      navigateTo(
        makePlusUrl(
          sharedData?.plus_crew?.domain_name,
          `/${ET.lang}/${this.data.creator.domain_name}/membership/main`,
        ),
        makeEmptyScreenNavigate,
      );
    };

    const goToMembershipProduct = () => {
      navigateTo(
        makePlusUrl(
          sharedData?.plus_crew?.domain_name,
          `/${ET.lang}/${this.data.creator.domain_name}/membership/products`,
        ),
        makeEmptyScreenNavigate,
      );
    };

    const goToCommissionMain = () => {
      navigateTo(
        makePlusUrl(
          sharedData?.plus_crew?.domain_name,
          `/${ET.lang}/${this.data.creator.domain_name}/membership/commission`,
        ),
        makeEmptyScreenNavigate,
      );
    };

    if (this.data.membership?.id) {
      try {
        const client = await getActivatedSdkWithEvent(this.data.membership.my.token);
        const widget = new CreatorFanServiceWidget({
          client,
          toggleSpinner: MembershipUtilS.toggleSpinner,
          isMobile: this.data.layout_options.is_mobile,
          projectId: this.data.membership.id,
          onError: this._membershipErrorEnable.onError,
          isApp: MShopUtilF.isApp(),
          onScrollLock: MShopUtilF.bodyFixed$,
          // 각 멤버십 상품 페이지로 이동
          onMembershipDimThumbnailClick: goToMembershipProduct,
          // 각 멤버십 상품 페이지로 이동
          onMembershipThumbnailClick: goToMembershipMain,
          // 각 멤버십 상품 페이지로 이동
          onMembershipJoinButtonClick: goToMembershipMain,
          // 각 리스트 페이지로 이동
          onCommissionThumbnailClick: goToCommissionMain,
          // 각 리스트 페이지로 이동
          onCommissionRequestButtonClick: goToCommissionMain,
        });
        const membership_wrap_elem = this.element().querySelector(`.${klass.membership_wrap}`) as HTMLElement;
        if (membership_wrap_elem) {
          widget.render(membership_wrap_elem);
        }
      } catch (e) {
        ConfigSentryF.error(e);
        await this._membershipErrorEnable.openWidgetRenderErrorModal();
      }
    }
  }
}

class StoreMainBody extends View<StoreMainPageData> {
  private fanLetterListView: FanLetterListView;

  constructor(data: StoreMainPageData) {
    super(data);
    const {
      fan_letters,
      creator,
      is_login,
      layout_options: { is_mobile },
    } = this.data;
    const adult_profile_image =
      ET.lang === 'kr'
        ? ASSET_URLS.CREATOR_SHOP_HOME.CREATOR_SHOP_HOME_ADULT_CREATOR_PROFILE_19
        : ASSET_URLS.CREATOR_SHOP_HOME.CREATOR_SHOP_HOME_ADULT_CREATOR_PROFILE_18;

    this.fanLetterListView = new FanLetterListView({
      fan_letters: fan_letters.slice(0, is_mobile ? 10 : 4),
      creator: {
        ...creator,
        thumbnail_img:
          creator.is_adult && data.adult_check_needed ? adult_profile_image : creator.thumbnail_img,
      },
      is_mobile,
      is_login,
      list_type: 'horizontal',
      item_mode: 'Large',
    });
    new FanLetterFormController(this.fanLetterListView.getFanLetterFormView());
    new FanLetterItemController(this.fanLetterListView.getFanLetterItemViews());
  }

  private static _makeSectionTitle({
    title,
    is_mobile,
    uri,
  }: {
    title: string;
    is_mobile: boolean;
    uri?: string;
  }) {
    return new SectionTitle({
      title_props: {
        title,
        size: is_mobile ? 'xxSmall' : 'semiMedium',
      },
      plugins: uri
        ? [
            new SeeMoreLinkPlugin({
              see_more_link: uri,
              navigate: makeEmptyScreenNavigate(uri),
              size: is_mobile ? 'small' : 'xLarge',
              show_icon: true,
            }),
          ]
        : [],
    });
  }

  protected override template(data: StoreMainPageData): Html {
    const {
      creator,
      adult_check_needed,
      photo_reviews,
      menus,
      popup,
      membership,
      is_login,
      layout_options,
      products,
      best_store_ranking,
      fan_letters,
      store,
    } = data;
    const { is_mobile } = data.layout_options;
    const sharedData = getSharedData(this);
    const isAllEndedFuning = FundingHelper.isAllEndedFunding(this.data.fundings?.data || []);

    const makeUri = (param: string) => {
      return sharedData?.plus_crew && isAllCrewById(sharedData?.plus_crew.id)
        ? `/${ET.lang}/${creator.domain_name}/${param}`
        : makePlusUrl(sharedData?.plus_crew?.domain_name, `/${ET.lang}/${creator.domain_name}/${param}`);
    };

    const MainBannerView = is_mobile ? CreatorShopMainBanner24MoView : CreatorShopMainBanner24View;

    const LnbView = is_mobile ? CreatorShopSubHeader : LnbCreatorShop24View;

    return html`
      <div class="${klass.page}">
        ${new MainBannerView({
          cover:
            (creator.is_adult && adult_check_needed) ||
            (is_mobile
              ? creator.banner_img_mo === CreatorConstantS.banner_img
              : creator.banner_img === CreatorConstantS.banner_img)
              ? 'No Cover'
              : 'Default',
          show_adult_creator_thumbnail: Boolean(creator.is_adult && adult_check_needed),
          creator,
          is_owner: creator.user_id === layout_options.user?.id,
          is_login,
          membership,
          best_store_ranking,
          store,
        })}
        <div class="${klass.sub_header}">
          ${new LnbView({
            menus,
            plus_domain: sharedData?.plus_crew?.domain_name,
            focus_on_select: true,
          })}
        </div>

        <div class="${klass.store_main_wrap}">
          <!-- 1. 멤버십 / 커미션 / 후원 -->
          ${this.data.membership?.id &&
          (membership?.membership_plan_existed || membership?.membership_commission_existed) &&
          !(creator.is_adult && adult_check_needed)
            ? html`
                <section class="${klass.section_wrapper}">
                  <div class="${klass.title}">
                    <span class="${classes(font('18_bold', '14_bold'))}"
                      >${ET('creator_shop::fan_service')}</span
                    >
                  </div>

                  ${new MembershipView({
                    ...this.data,
                  })}
                </section>
              `
            : ''}

          <!-- 2. 굿즈 -->
          <!-- 3. 핸드메이드 -->
          <!-- 4. 브랜드딜 -->
          <!-- 5. 전자책 -->
          ${(() => {
            if (
              products.goods.length === 0 &&
              products.handmade.length === 0 &&
              products.brand_deal.length === 0 &&
              products.ebook.length === 0
            ) {
              return new CreatorShopProductListEmptyScreen({
                hide_footer: is_mobile,
                klass: is_mobile ? klass.creator_shop_product_list_empty_screen : undefined,
              });
            } else {
              return [products.goods, products.handmade, products.brand_deal, products.ebook].map(
                (products, i) =>
                  products.length > 0
                    ? html`
                        <div class="${klass.section_wrapper}">
                          <div class="${klass.title}">
                            ${products.length <
                            (is_mobile
                              ? MOBILE_SHOW_PRODUCT_SEE_MORE_THRESHOLD
                              : SHOW_PRODUCT_SEE_MORE_THRESHOLD)
                              ? html`<span class="${classes(font('18_bold', '14_bold'))}">
                                  ${[
                                    ET('creator_shop::section_title::goods_products'),
                                    ET('creator_shop::section_title::handmade_products'),
                                    ET('creator_shop::section_title::brand_deal_products'),
                                    ET('creator_shop::section_title::ebook_products'),
                                  ][i]}
                                </span>`
                              : StoreMainBody._makeSectionTitle({
                                  title: [
                                    ET('creator_shop::section_title::goods_products'),
                                    ET('creator_shop::section_title::handmade_products'),
                                    ET('creator_shop::section_title::brand_deal_products'),
                                    ET('creator_shop::section_title::ebook_products'),
                                  ][i]!,
                                  is_mobile,
                                  uri: makeUri(
                                    [
                                      `products?cate_root_id=${ECateRootId.Goods}`,
                                      `products?cate_root_id=${ECateRootId.Handmade}`,
                                      `products?cate_root_id=${ECateRootId.BrandDeal}`,
                                      `products?cate_root_id=${ECateRootId.Ebook}`,
                                    ][i]!,
                                  ),
                                })}
                          </div>
                          <div class="${klass.list}">
                            ${pipe(
                              products,
                              map(
                                (product) =>
                                  new CardProductControllerView({
                                    card_product_view_props: {
                                      type:
                                        product.category.root_id === ECateRootId.Goods ||
                                        product.category.root_id === ECateRootId.Handmade
                                          ? 'C'
                                          : 'G',
                                      size: is_mobile ? 'Medium' : 'Large',
                                      layout: 'Vertical',
                                      theme: 'Light',
                                    },
                                    product_card_view_data: product,
                                    options: {
                                      resize_width: 600,
                                      is_lazy: true,
                                    },
                                  }),
                              ),
                              take(20),
                              toArray,
                            )}
                          </div>
                        </div>
                      `
                    : null,
              );
            }
          })()}

          <!-- 6. 펀딩 -->
          ${doIf(
            () => html`
              <section
                class="${classes(klass.section_wrapper, {
                  [klass.has_active_fundings]: !isAllEndedFuning,
                })}"
              >
                <div class="${klass.title}">
                  ${StoreMainBody._makeSectionTitle({
                    title: ET('preorder::section_title_pre_order_challenge'),
                    is_mobile,
                    uri:
                      this.data.fundings?.data && this.data.fundings?.data.length > 2
                        ? makeUri('fundings')
                        : undefined,
                  })}
                </div>

                <div
                  class="${classes(klass.fundings, {
                    [klass.double]: (this.data.fundings?.data || []).length > 1,
                  })}"
                >
                  ${pipe(
                    this.data.fundings?.data || [],
                    take(2),
                    map(
                      (funding) =>
                        new FundingCard({
                          goodsTypeId: funding.goodsTypeId,
                          product_id: funding.productId,
                          domain_name: funding.domainName,
                          name: funding.name,
                          description: funding.description,
                          goal_amount: funding.goalAmount,
                          total_amount: funding.totalAmount,
                          thumbnail_info: funding.thumbnailInfo,
                          sell_start_at: funding.sellStartAt,
                          sell_end_at: funding.sellEndAt,
                          is_quantity_public: funding.isQuantityPublic,
                          is_public: funding.isPublic,
                          is_adult: funding.isAdult,
                          order_count: funding.orderCount,
                          quantity: funding.quantity,
                          out_of_stock: funding.outOfStock,
                          server_timestamp: this.data.server_timestamp,
                          is_mobile,
                          width_full: true,
                          userId: this.data.layout_options.user?.id,
                          storesProductId: funding.storesProductId,
                          isLiked: funding.isLiked,
                          showAdultThumbnail: adult_check_needed && funding.isAdult,
                        }),
                    ),
                    toArray,
                  )}
                </div>
              </section>
            `,
            Boolean(this.data.fundings?.data.length),
          )}

          <!-- 7. 팬레터 -->
          <div class="${klass.section_wrapper} ">
            <div class="${klass.title}">
              ${fan_letters.length <
              (is_mobile ? MOBILE_SHOW_FAN_LETTER_SEE_MORE_THRESHOLD : SHOW_FAN_LETTER_SEE_MORE_THRESHOLD)
                ? html`<span class="${classes(font('18_bold', '14_bold'))}">
                    ${ET('creator_shop::fan_letters::title')}
                  </span>`
                : StoreMainBody._makeSectionTitle({
                    title: ET('creator_shop::fan_letters::title'),
                    is_mobile,
                    uri: makeUri('fan-letter'),
                  })}
            </div>
            <div class="${klass.fan_letter_list_view}">${this.fanLetterListView}</div>
          </div>

          <!-- 8. 리뷰 -->
          ${photo_reviews.length > 0
            ? html`
                <div class="${klass.section_wrapper}">
                  <div class="${klass.title}">
                    ${photo_reviews.length <
                    (is_mobile ? MOBILE_SHOW_REVIEW_SEE_MORE_THRESHOLD : SHOW_REVIEW_SEE_MORE_THRESHOLD)
                      ? html`<span class="${classes(font('18_bold', '14_bold'))}">
                          ${ET('creator_shop::section_title::reviews')}
                        </span>`
                      : StoreMainBody._makeSectionTitle({
                          title: ET('creator_shop::section_title::reviews'),
                          is_mobile,
                          uri: makeUri('review'),
                        })}
                  </div>
                  ${new PhotoReviewList(photo_reviews.slice(0, is_mobile ? 10 : 5))}
                </div>
              `
            : ''}
          ${popup.popups.length > 0 ? new Popup(popup) : ''}
        </div>
      </div>
    `;
  }

  protected override onRender() {
    const is_mobile = !!getSharedData(this)?.is_mobile;
    const sub_header_view = this.subView(CreatorShopSubHeader);

    if (sub_header_view && is_mobile) {
      applySubHeaderHeadroom(HeaderCreatorShop, sub_header_view, { fixed: true });
    }
  }
}

export class StoreMainPage extends Page<StoreMainPageData> {
  protected override template(data: StoreMainPageData): Html {
    const sharedData = getSharedData(this);
    const { layout_options, recent_product } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new StoreMainBody(data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: true,
            has_bottom_tab_bar: !sharedData?.webviewapp,
            selected_bottom_tab_idx: -1,
            floating_box: {
              recent_product: FloatingButton_recent_product.makeProps(recent_product),
              share: {
                title: sanitizer.unescapeEntities(data.creator.name) || 'Creator',
                description: ET('mps2::share::store_main_description', {
                  creator_name: data.creator.name || 'creator',
                }),
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
